import React from 'react'
import { styled } from '@mui/material/styles'
import { RaRecord } from 'react-admin'
import {
  PricingMinHoursRule,
  PricingRuleMode,
  PricingTimePeriodRule,
} from 'services/api/entities/catalog/types'
import { TypedChoices, pricingRulesModeChoices } from 'services/cfg/form'
import KToolTip from 'ui-shared/KTooltip'
import HalfStepNumberInput from 'ui-shared/WrappedInputs/HalfStepNumberInput'
import TimePicker from 'ui-shared/WrappedInputs/TimePicker'
import { useFormContext } from 'react-hook-form'
import {
  ArrayInputContentProps,
  useKArrayInput,
} from 'services/hooks/useKArrayInput'
import { LocationPricingRuleSet } from 'services/api/entities/catalog/location/types/internal/location-models'
import KSelectInput from 'ui-shared/WrappedInputs/KSelectInput'

const PREFIX = 'PricingRuleIfClauseInputGroup'

const classes = {
  container: `${PREFIX}-container`,
  pricingContainer: `${PREFIX}-pricingContainer`,
  type: `${PREFIX}-type`,
  modifiers: `${PREFIX}-modifiers`,
  sectionTitle: `${PREFIX}-sectionTitle`,
  divider: `${PREFIX}-divider`,
  marginBottom: `${PREFIX}-marginBottom`,
  flex: `${PREFIX}-flex`,
  w100: `${PREFIX}-w100`,
  marginRight: `${PREFIX}-marginRight`,
  marginTop: `${PREFIX}-marginTop`,
  fieldsBox: `${PREFIX}-fieldsBox`,
  fieldWidth: `${PREFIX}-fieldWidth`,
  halfFieldWidth: `${PREFIX}-halfFieldWidth`,
  ml55: `${PREFIX}-ml55`,
  clauseText: `${PREFIX}-clauseText`,
}

const StyledDiv = styled('div')(({ theme }: any) => ({
  [`& .${classes.container}`]: {
    marginTop: '24px',
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.pricingContainer}`]: {
    width: '100%',
    marginTop: '24px',
    display: 'flex',
    gap: '400px',
  },

  [`& .${classes.type}`]: {
    minWidth: '100px',
  },

  [`& .${classes.modifiers}`]: {
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.sectionTitle}`]: {
    fontWeight: 'bold',
    fontSize: '20px',
    marginTop: '24px',
    textTransform: 'capitalize',
  },

  [`& .${classes.divider}`]: {
    width: '100%',
    height: '3px',
    backgroundColor: theme.palette.primary.light,
    marginTop: '20px',
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: '16px',
  },

  [`& .${classes.flex}`]: {
    display: 'flex',
  },

  [`& .${classes.w100}`]: {
    width: '100%',
  },

  [`& .${classes.marginRight}`]: {
    marginRight: '16px',
  },

  [`& .${classes.marginTop}`]: {
    marginTop: '16px',
  },

  [`&.${classes.fieldsBox}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '100px',
    width: '100%',
  },

  [`& .${classes.fieldWidth}`]: {
    width: '300px',
  },

  [`& .${classes.halfFieldWidth}`]: {
    width: '150px',
  },

  [`& .${classes.ml55}`]: {
    marginLeft: '50px',
  },

  [`& .${classes.clauseText}`]: {
    fontSize: '20px',
    fontWeight: 'bold',
    width: '10px',
    marginBottom: '20px',
  },
}))

type Props = ArrayInputContentProps & {
  ruleSet?: LocationPricingRuleSet
  nameChoices: TypedChoices<string | undefined>
}

const PricingRuleIfClauseInputGroup: React.FC<Props> = ({
  source,
  ruleSet,
  nameChoices,
}) => {
  const { unregister, setValue, watch } = useFormContext()

  const { kGetSource, scopedFormData, index } = useKArrayInput<
    PricingTimePeriodRule | PricingMinHoursRule
  >(source!)

  const isFirstNHR =
    ruleSet?.pricingRules?.findIndex((r) => r?.mode === 'nhr') === index

  const atLeastValue = watch(kGetSource('atLeast'))

  // this is not a default value because if I delete the first one
  // and I have another already set this one don't use the default value
  if (isFirstNHR && atLeastValue !== 0.5) setValue(kGetSource('atLeast'), 0.5)

  const resetMode = ({
    target: { value },
  }: React.ChangeEvent<{ value: any }> | RaRecord) => {
    const newMode = value as PricingRuleMode

    if (newMode === 'nhr') {
      unregister(kGetSource('hoursFrom'))
      unregister(kGetSource('hoursTo'))
    } else {
      unregister(kGetSource('atLeast'))
    }
  }

  return (
    <StyledDiv className={classes.fieldsBox}>
      <span className={classes.clauseText}>IF</span>
      <KSelectInput
        label="form.fields.ruleWhat"
        source={kGetSource('mode')}
        defaultValue={pricingRulesModeChoices[1].id} // 'nhr'
        choices={pricingRulesModeChoices}
        className={classes.fieldWidth}
        onChange={resetMode}
      />

      <span className={classes.clauseText}>IS</span>
      {scopedFormData?.mode === 'tpr' ? (
        <div className={classes.flex}>
          <TimePicker
            source={kGetSource('hoursFrom')}
            label="From"
            className={classes.halfFieldWidth}
          />
          <TimePicker
            source={kGetSource('hoursTo')}
            label="To"
            className={classes.halfFieldWidth}
            style={{ marginLeft: '4px' }}
          />
        </div>
      ) : (
        <HalfStepNumberInput
          source={kGetSource('atLeast')}
          label="More than hours"
          disabled={isFirstNHR}
          className={classes.fieldWidth}
        />
      )}
      <KToolTip title="tooltip.pricingRule.pricingName" placement="top">
        <KSelectInput
          source={kGetSource('name')}
          label="Price name"
          defaultValue={nameChoices[0].id}
          choices={nameChoices}
          className={classes.fieldWidth}
        />
      </KToolTip>
    </StyledDiv>
  )
}

export default PricingRuleIfClauseInputGroup
